import { c as _c } from "react/compiler-runtime";
/**
 * Run Contract Service
 * PRD-45: Backend-driven flow runs with canonical event schemas
 *
 * Adapts the current FastAPI v2 flow contract into the older Office UI shape
 * so the trading floor can run against real backend executions without a broad
 * frontend rewrite.
 */

import { apiClient } from "@/services/api-client";
import { debugService } from "@/features/debug";

// =============================================================================
// Types - Matching backend run_contract.py schemas
// =============================================================================

/** Node status enum matching backend NodeStatus */
export type NodeStatus = "idle" | "running" | "success" | "error" | "waiting_hitl" | "skipped" | "filtered";

/** Flow run status enum matching backend RunStatus */
export type RunStatus = "pending" | "running" | "completed" | "rejected" | "failed" | "cancelled";

/** Automation level matching backend AutomationLevelChoices */
export type AutomationLevel = "manual" | "semi_automated" | "fully_automated" | "sigma_autonomy";

/** Artifact type enum */
export type ArtifactType = "screenshot" | "indicator_data" | "analysis_result" | "error_log";

/** Artifact schema matching backend ArtifactSchema */
export interface Artifact {
  artifact_id: string;
  run_id: string;
  node_id: string;
  artifact_type: ArtifactType;
  url: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

/** MTF Timeframe result from Ben Kim */
export interface MTFTimeframeResult {
  timeframe: string;
  screenshot_url: string | null;
  visual_score: number;
  trend_direction: "bullish" | "bearish" | "neutral";
  indicators: Record<string, number | null> | null;
  price_vs_vwap?: "above" | "below" | "at";
  price_vs_sr?: "near_support" | "near_resistance" | "between_levels";
  reasoning: string;
  captured_at?: string;
}

/** Node snapshot in a run */
export interface NodeSnapshot {
  node_id: string;
  name: string;
  status: NodeStatus;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  agent_name: string | null;
  agent_avatar: string | null;
  agent_key?: string | null; // Agent persona identifier for grouping (e.g., "wendy", "wags")
  message: string | null;
  data: {
    label: string;
    value: string;
  }[] | null;
  artifacts: Artifact[];
  /** Ben Kim specific: MTF analysis results */
  mtf_timeframes?: MTFTimeframeResult[];
  overall_score?: number;
  overall_direction?: "bullish" | "bearish" | "neutral";
  conflicting_timeframes?: string[];
}

/** Full run snapshot for hydration */
export interface RunSnapshot {
  run_id: string;
  status: RunStatus;
  automation_level: AutomationLevel;
  started_at: string;
  completed_at: string | null;
  current_node_id: string | null;
  nodes: NodeSnapshot[];
  error_message: string | null;
  execution_path: "api" | "browser" | null;
  hitl_pending: boolean;
  hitl_request_id: string | null;
}
export interface RunListItem {
  run_id: string;
  status: RunStatus;
  automation_level: AutomationLevel;
  symbol: string;
  started_at: string;
  completed_at: string | null;
  current_node: string | null;
  execution_time_ms: number | null;
  error: string | null;
}

/** Request to start a new flow run */
export interface StartRunRequest {
  /** Optional: Override default automation level for this run */
  automation_level_override?: AutomationLevel;
  /** Optional: Explicit broker environment for execution bootstrap */
  environment_override?: "demo" | "live";
  /** Internal SigmaVue broker account ID selected for this run */
  broker_account_id?: number | null;
  /** Active trading group whose leader is the selected broker account */
  trading_group_id?: number | null;
  /** Numeric strategy registry ID (only when truly numeric) */
  strategy_id?: string | number;
  /** Catalog / Lab strategy slug for Carter bind */
  strategy_slug?: string | null;
  /** Symbol to trade */
  symbol?: string;
  /** Signal direction */
  direction?: "long" | "short";
  /** Additional context for the run */
  context?: Record<string, unknown>;
}
function resolveNumericStrategyId(value: string | number | undefined): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return Number(value.trim());
  }
  return null;
}
function resolveStrategySlug(request: StartRunRequest): string | null {
  if (typeof request.strategy_slug === "string" && request.strategy_slug.trim()) {
    return request.strategy_slug.trim();
  }
  const fromContext = request.context?.strategy_slug;
  if (typeof fromContext === "string" && fromContext.trim()) {
    return fromContext.trim();
  }
  return null;
}

/** Response from starting a run */
export interface StartRunResponse {
  success: boolean;
  run_id: string;
  message: string;
  websocket_url?: string;
  stream_url?: string;
  initial_snapshot?: RunSnapshot;
  error?: string;
}
interface BackendNodeSnapshot {
  node_id: string;
  node_name: string;
  status: "pending" | "running" | "success" | "rejected" | "skipped" | "error";
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number;
  summary: string;
  output: Record<string, unknown>;
  artifacts: Record<string, unknown> | null;
}
interface BackendRunSnapshot {
  run_id: string;
  user_id: number;
  status: "pending" | "running" | "paused" | "completed" | "rejected" | "error" | "cancelled";
  automation_level: AutomationLevel;
  symbol: string;
  started_at: string;
  completed_at: string | null;
  nodes: BackendNodeSnapshot[];
  current_node: string | null;
  hitl_pending: boolean;
  hitl_request_id?: string | null;
}
interface BackendRunSummary {
  run_id: string;
  user_id: number;
  status: "pending" | "running" | "paused" | "completed" | "rejected" | "error" | "cancelled";
  automation_level: AutomationLevel;
  symbol: string;
  started_at: string;
  completed_at: string | null;
  current_node: string | null;
  execution_time_ms: number | null;
  error: string | null;
}
interface BackendRunSummaryListResponse {
  runs: BackendRunSummary[];
}
interface BackendStartFlowResponse {
  run_id: string;
  websocket_url: string;
  stream_url: string;
  initial_snapshot: BackendRunSnapshot;
}

// =============================================================================
// WebSocket Event Types - Matching backend event_broadcaster.py
// =============================================================================

/** Base event structure */
export interface FlowEvent {
  event_type: string;
  run_id: string;
  timestamp: string;
  correlation_id: string;
}

/** Flow started event */
export interface FlowStartedEvent extends FlowEvent {
  event_type: "flow_started";
  automation_level: AutomationLevel;
  initial_context: Record<string, unknown>;
}

/** Node started event */
export interface NodeStartedEvent extends FlowEvent {
  event_type: "node_started";
  node_id: string;
  node_name: string;
  agent_name: string | null;
  agent_avatar: string | null;
  agent_key?: string | null; // Agent persona identifier for grouping
}

/** Node completed event */
export interface NodeCompletedEvent extends FlowEvent {
  event_type: "node_completed";
  node_id: string;
  node_name: string;
  status: NodeStatus;
  duration_ms: number;
  agent_key?: string | null; // Agent persona identifier for grouping
  /**
   * SIG-872: the node's display rows, clipped for the wire. Named `output` to
   * match what the backend actually publishes (event_broadcaster.node_completed).
   * The field this replaced, `result_data`, had zero backend writers — the type
   * described a payload that never existed.
   */
  output: Record<string, string>;
  summary: string;
  artifacts: Artifact[];
  message: string | null;
}

/** Flow completed event */
export interface FlowCompletedEvent extends FlowEvent {
  event_type: "flow_completed";
  final_status: RunStatus;
  total_duration_ms: number;
  nodes_executed: number;
  execution_path: "api" | "browser" | null;
}

/** Flow error event */
export interface FlowErrorEvent extends FlowEvent {
  event_type: "flow_error";
  node_id: string | null;
  error_type: string;
  error_message: string;
  recoverable: boolean;
  auto_heal_trigger: string | null;
}

/** HITL required event */
export interface HITLRequiredEvent extends FlowEvent {
  event_type: "hitl_required";
  hitl_request_id: string;
  expires_at: string;
  signal_snapshot: Record<string, unknown>;
}

/** Auto-heal trigger event */
export interface AutoHealTriggerEvent extends FlowEvent {
  event_type: "auto_heal_trigger";
  trigger_type: string;
  reason: string;
}

/** Union of all flow events */
export type FlowEventUnion = FlowStartedEvent | NodeStartedEvent | NodeCompletedEvent | FlowCompletedEvent | FlowErrorEvent | HITLRequiredEvent | AutoHealTriggerEvent;
function normalizeAutomationLevel(level?: AutomationLevel): "manual" | "semi_automated" | "fully_automated" | undefined {
  if (!level) return undefined;
  if (level === "sigma_autonomy") {
    return "semi_automated";
  }
  return level;
}
function adaptNodeStatus(status: BackendNodeSnapshot["status"], hitlPending: boolean): NodeStatus {
  if (hitlPending && status === "running") {
    return "waiting_hitl";
  }
  switch (status) {
    case "pending":
      return "idle";
    case "running":
      return "running";
    case "success":
      return "success";
    case "skipped":
      return "skipped";
    case "rejected":
      // A rejected node is a NO-TRADE verdict — the agent did its job and
      // concluded "don't trade." Only real failures may render as error.
      return "filtered";
    case "error":
      return "error";
    default:
      return "idle";
  }
}
function adaptArtifacts(artifacts: BackendNodeSnapshot["artifacts"], runId: string, nodeId: string): Artifact[] {
  if (!artifacts || typeof artifacts !== "object") {
    return [];
  }
  const artifactTypes = new Set<ArtifactType>(["screenshot", "indicator_data", "analysis_result", "error_log"]);
  return Object.entries(artifacts).flatMap(([artifactType, rawArtifact]) => {
    if (!artifactTypes.has(artifactType as ArtifactType) || !rawArtifact || typeof rawArtifact !== "object" || Array.isArray(rawArtifact)) {
      return [];
    }
    const artifact = rawArtifact as Record<string, unknown>;
    if (typeof artifact.artifact_id !== "string" || !artifact.artifact_id || typeof artifact.url !== "string" || !artifact.url || typeof artifact.created_at !== "string" || !artifact.created_at) {
      return [];
    }
    const metadata = artifact.metadata && typeof artifact.metadata === "object" && !Array.isArray(artifact.metadata) ? artifact.metadata as Record<string, unknown> : {};
    return [{
      artifact_id: artifact.artifact_id,
      run_id: runId,
      node_id: nodeId,
      artifact_type: artifactType as ArtifactType,
      url: artifact.url,
      metadata,
      created_at: artifact.created_at
    }];
  });
}
function adaptNodeData(output: Record<string, unknown>): {
  label: string;
  value: string;
}[] | null {
  const entries = Object.entries(output || {}).filter(([, value]) => value != null);
  if (entries.length === 0) {
    return null;
  }
  return entries.slice(0, 8).map(([label, value]) => ({
    label,
    value: typeof value === "string" ? value : JSON.stringify(value)
  }));
}
function adaptRunStatus(status: BackendRunSnapshot["status"]): RunStatus {
  switch (status) {
    case "error":
      return "failed";
    case "rejected":
      return "rejected";
    case "paused":
      return "running";
    default:
      return status;
  }
}
function adaptRunSnapshot(snapshot: BackendRunSnapshot): RunSnapshot {
  return {
    run_id: snapshot.run_id,
    status: adaptRunStatus(snapshot.status),
    automation_level: snapshot.automation_level,
    started_at: snapshot.started_at,
    completed_at: snapshot.completed_at,
    current_node_id: snapshot.current_node,
    nodes: snapshot.nodes.map(node => ({
      node_id: node.node_id,
      name: node.node_name,
      status: adaptNodeStatus(node.status, snapshot.hitl_pending && snapshot.current_node === node.node_id),
      started_at: node.started_at,
      completed_at: node.completed_at,
      duration_ms: node.duration_ms,
      agent_name: null,
      agent_avatar: null,
      message: node.summary || null,
      data: adaptNodeData(node.output),
      artifacts: adaptArtifacts(node.artifacts, snapshot.run_id, node.node_id)
    })),
    error_message: null,
    execution_path: null,
    hitl_pending: snapshot.hitl_pending,
    hitl_request_id: snapshot.hitl_request_id ?? null
  };
}
function adaptRunSummary(summary: BackendRunSummary): RunListItem {
  return {
    run_id: summary.run_id,
    status: adaptRunStatus(summary.status),
    automation_level: summary.automation_level,
    symbol: summary.symbol,
    started_at: summary.started_at,
    completed_at: summary.completed_at,
    current_node: summary.current_node,
    execution_time_ms: summary.execution_time_ms,
    error: summary.error
  };
}

// =============================================================================
// API Service
// =============================================================================

/**
 * Start a new flow run
 */
export async function startFlowRun(request: StartRunRequest = {}): Promise<StartRunResponse> {
  const strategySlug = resolveStrategySlug(request);
  const {
    data,
    status
  } = await apiClient.postV2<BackendStartFlowResponse>("/flow/runs/", {
    symbol: request.symbol || "MNQ",
    strategy_id: resolveNumericStrategyId(request.strategy_id),
    strategy_slug: strategySlug,
    automation_override: normalizeAutomationLevel(request.automation_level_override),
    environment_override: request.environment_override,
    broker_account_id: request.broker_account_id ?? null,
    trading_group_id: request.trading_group_id ?? null,
    demo_signal: request.context?.demo_signal
  }, {
    trailingSlash: true
  });
  if (status >= 400) {
    throw new Error((data as unknown as {
      error?: string;
    })?.error || `Failed to start run: ${status}`);
  }
  return {
    success: true,
    run_id: data.run_id,
    message: "Flow run started",
    websocket_url: data.websocket_url,
    stream_url: data.stream_url,
    initial_snapshot: adaptRunSnapshot(data.initial_snapshot)
  };
}

/**
 * Get run snapshot for hydration/replay
 */
export async function getRunSnapshot(runId: string): Promise<RunSnapshot> {
  const {
    data,
    status
  } = await apiClient.getV2<BackendRunSnapshot>(`/flow/runs/${runId}`);
  if (status >= 400) {
    throw new Error((data as unknown as {
      error?: string;
    })?.error || `Failed to get run snapshot: ${status}`);
  }
  return adaptRunSnapshot(data);
}

/**
 * List recent flow runs for Office history/bootstrap.
 */
export async function listFlowRuns(limit = 20): Promise<RunListItem[]> {
  const {
    data,
    status
  } = await apiClient.getV2<BackendRunSummaryListResponse>("/flow/runs/", {
    params: {
      limit
    }
  });
  if (status >= 400) {
    throw new Error((data as unknown as {
      error?: string;
    })?.error || `Failed to list runs: ${status}`);
  }
  return (data.runs || []).map(adaptRunSummary);
}

/** Persisted transcript event (SV-18 agent reasoning surface). */
export interface TranscriptEvent {
  event_id: string;
  id: string;
  run_id: string;
  from_agent: string;
  to_agent: string | null;
  event_type: string;
  node_name: string | null;
  content: string;
  metadata: Record<string, unknown>;
  timestamp: string | null;
}

/**
 * SIG-289: a forbidden transcript (someone else's run / no access) must be
 * distinguishable from an empty one — the store maps this error to the
 * "unavailable" state instead of rendering an empty feed.
 */
export class TranscriptForbiddenError extends Error {
  constructor(runId: string) {
    super(`Transcript for run ${runId} is forbidden`);
    this.name = "TranscriptForbiddenError";
  }
}

/**
 * Fetch the persisted transcript for a run (GET /api/flows/{run_id}/transcript).
 *
 * SIG-289 result contract (PR #519 r2): HTTP 403 throws
 * TranscriptForbiddenError (the run belongs to someone else — the store maps
 * it to the "unavailable" state); any OTHER failure returns null so a fetch
 * problem is never presented as a genuinely-empty transcript; [] means the
 * run truly has no persisted events.
 */
export async function getRunTranscript(runId: string, limit = 200): Promise<TranscriptEvent[] | null> {
  try {
    const {
      data,
      status
    } = await apiClient.getWithSuccess<{
      events?: TranscriptEvent[];
    }>(`/flows/${runId}/transcript`, {
      params: {
        limit
      }
    });
    if (status === 403) throw new TranscriptForbiddenError(runId);
    if (status >= 400) return null;
    return Array.isArray(data?.events) ? data.events : [];
  } catch (error) {
    if (error instanceof TranscriptForbiddenError) throw error;
    logRunContractError("Failed to fetch run transcript", error);
    return null;
  }
}

/** HITL resume action */
export type HITLAction = "approve" | "reject" | "modify";

/** Response from resuming a run */
export interface ResumeRunResponse {
  success: boolean;
  message: string;
  initial_snapshot?: RunSnapshot;
}
function logRunContractError(message: string, error?: unknown) {
  debugService.log("error", message, error ? {
    error
  } : undefined);
}

/**
 * Resume a paused flow run after HITL decision
 */
export async function resumeFlowRun(runId: string, action: HITLAction, modifications?: Record<string, unknown>): Promise<ResumeRunResponse> {
  const {
    data,
    status
  } = await apiClient.postV2<BackendRunSnapshot>(`/flow/runs/${runId}/resume`, {
    action,
    modifications: modifications || {}
  });
  if (status >= 400) {
    throw new Error(`Failed to resume run: ${status}`);
  }
  return {
    success: true,
    message: "Flow resumed",
    initial_snapshot: data ? adaptRunSnapshot(data) : undefined
  };
}

// =============================================================================
// React Query Hooks
// =============================================================================

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/** Query keys for run contract data */
export const runContractQueryKeys = {
  all: ["runContract"] as const,
  snapshot: (runId: string) => [...runContractQueryKeys.all, "snapshot", runId] as const
};

/**
 * Hook to fetch run snapshot
 */
export function useRunSnapshot(runId) {
  const $ = _c(8);
  let t0;
  if ($[0] !== runId) {
    t0 = runContractQueryKeys.snapshot(runId || "");
    $[0] = runId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== runId) {
    t1 = () => runId ? getRunSnapshot(runId) : null;
    $[2] = runId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!runId;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 5000
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}

/**
 * Hook to start a new flow run
 */
export function useStartFlowRun() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: startFlowRun,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["flow", "current"]
        });
      },
      onError: _temp
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp(error) {
  logRunContractError("[RunContract] Failed to start run", error);
}